// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Starts loading a site's plan approvals as soon as the site resolves, rather
 * than waiting for the Recommendations or Optimizations screen to mount.
 *
 * Mounted once in the app shell, so it covers every way the active site can
 * change (header dropdown, fleet tiles, URL sync — all write the same
 * `selectedSiteId`) and fires on the first authenticated paint once the
 * persisted/default selection resolves.
 *
 * Nothing awaits the prefetch, so it never blocks the shell. It warms the same
 * cache entry `useSiteApprovals` reads, so by the time the user opens either
 * screen the list is cached, or at worst the page joins a request already in
 * flight and shows a background indicator instead of a blocking spinner.
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { approvalsQueryOptions } from './useApprovals';
import { useActiveSite } from './useActiveSite';
import { HITL_APPROVAL_TYPE } from '../utils/constants';
import { oneMonthAgoStartOfDayIso } from '../utils/time';

export function useApprovalsPrefetch(): void {
  const queryClient = useQueryClient();
  const { siteId } = useActiveSite();

  useEffect(() => {
    if (!siteId) return;
    void queryClient.prefetchQuery(
      approvalsQueryOptions(siteId, HITL_APPROVAL_TYPE, oneMonthAgoStartOfDayIso())
    );
  }, [queryClient, siteId]);
}
